<template>
  <v-navigation-drawer
    permanent
    right
    absolute
    width="320"
    class="floor-plan-properties-panel"
  >
    <v-list dense>
      <!-- No selection state -->
      <div v-if="!selectedObject" class="pa-4 text-center grey--text">
        <v-icon large color="grey lighten-1">mdi-cursor-default</v-icon>
        <p class="mt-2">{{ $t('$vuetify.floorPlan.noSelection') }}</p>
      </div>

      <!-- Object properties -->
      <div v-else>
        <v-subheader class="properties-subheader">
          {{ $t('$vuetify.floorPlan.properties') }}
          <v-btn icon x-small class="ml-2 help-btn" @click="propertiesHelpDialog = true">
            <v-icon small color="#3989C9">mdi-help-circle</v-icon>
          </v-btn>
        </v-subheader>

        <!-- Object type badge -->
        <v-list-item>
          <v-chip small :color="getTypeColor(selectedObject.type)" dark>
            {{ getTypeLabel(selectedObject.type) }}
          </v-chip>
        </v-list-item>

        <!-- Position -->
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle class="text-caption mb-1">
              {{ $t('$vuetify.floorPlan.position') }}
            </v-list-item-subtitle>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  :value="selectedObject.x"
                  @input="updateProperty('x', parseFloat($event))"
                  label="X"
                  type="number"
                  dense
                  outlined
                  hide-details
                  suffix="px"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  :value="selectedObject.y"
                  @input="updateProperty('y', parseFloat($event))"
                  label="Y"
                  type="number"
                  dense
                  outlined
                  hide-details
                  suffix="px"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-list-item-content>
        </v-list-item>

        <!-- Size (for tables and areas) -->
        <v-list-item v-if="selectedObject.type !== 'wall'">
          <v-list-item-content>
            <v-list-item-subtitle class="text-caption mb-1">
              {{ $t('$vuetify.floorPlan.size') }}
            </v-list-item-subtitle>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  :value="selectedObject.width"
                  @input="updateProperty('width', parseFloat($event))"
                  label="W"
                  type="number"
                  dense
                  outlined
                  hide-details
                  suffix="px"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  :value="selectedObject.height"
                  @input="updateProperty('height', parseFloat($event))"
                  label="H"
                  type="number"
                  dense
                  outlined
                  hide-details
                  suffix="px"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-list-item-content>
        </v-list-item>

        <!-- Rotation (for tables) -->
        <v-list-item v-if="selectedObject.type === 'table'">
          <v-list-item-content>
            <v-list-item-subtitle class="text-caption mb-1">
              {{ $t('$vuetify.floorPlan.rotation') }}
            </v-list-item-subtitle>
            <v-slider
              :value="selectedObject.rotation || 0"
              @input="updateProperty('rotation', $event)"
              min="0"
              max="360"
              step="15"
              thumb-label
              dense
              hide-details
            >
              <template v-slot:append>
                <span class="text-caption">{{ selectedObject.rotation || 0 }}°</span>
              </template>
            </v-slider>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Table-specific properties -->
        <div v-if="selectedObject.type === 'table'">
          <v-subheader>{{ $t('$vuetify.floorPlan.tableProperties') }}</v-subheader>

          <!-- Linked Table (moved to top) -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.linkedTable') }}
              </v-list-item-subtitle>
              <v-btn
                block
                class="link-table-btn"
                elevation="0"
                small
                @click="$emit('link-table', selectedObject)"
              >
                <v-icon left small>mdi-link</v-icon>
                {{ selectedObject.tableId ? $t('$vuetify.floorPlan.changeTable') : $t('$vuetify.floorPlan.linkTable') }}
              </v-btn>
              <div v-if="selectedObject.tableId" class="text-caption mt-1 grey--text">
                {{ $t('$vuetify.floorPlan.linked') }}
              </div>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.tableName"
                @input="updateProperty('tableName', $event)"
                :label="$t('$vuetify.floorPlan.tableName')"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-select
                :value="selectedObject.shape"
                @input="updateProperty('shape', $event)"
                :label="$t('$vuetify.floorPlan.shape')"
                :items="shapeOptions"
                dense
                outlined
                hide-details
              ></v-select>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.seats || 4"
                @input="updateProperty('seats', parseInt($event))"
                :label="$t('$vuetify.floorPlan.seats')"
                type="number"
                dense
                outlined
                hide-details
                min="1"
                max="20"
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.tableColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.color"
                @input="updateProperty('color', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.chairColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.chairColor || '#8B4513'"
                @input="updateProperty('chairColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <!-- Corner angle for corner tables -->
          <v-list-item v-if="selectedObject.shape === 'corner'">
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.cornerAngle') }}
              </v-list-item-subtitle>
              <v-select
                :value="selectedObject.cornerAngle || 0"
                @input="updateProperty('cornerAngle', $event)"
                :items="cornerAngleOptions"
                dense
                outlined
                hide-details
              ></v-select>
            </v-list-item-content>
          </v-list-item>
        </div>

        <!-- Wall-specific properties -->
        <div v-if="selectedObject.type === 'wall'">
          <v-subheader>{{ $t('$vuetify.floorPlan.wallProperties') }}</v-subheader>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.thickness') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.thickness"
                @input="updateProperty('thickness', $event)"
                min="5"
                max="30"
                step="5"
                thumb-label
                dense
                hide-details
              >
                <template v-slot:append>
                  <span class="text-caption">{{ selectedObject.thickness }}px</span>
                </template>
              </v-slider>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.color') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.color"
                @input="updateProperty('color', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>
        </div>

        <!-- Door-specific properties -->
        <div v-if="selectedObject.type === 'door'">
          <v-subheader>{{ $t('$vuetify.floorPlan.doorProperties') }}</v-subheader>

          <v-list-item>
            <v-list-item-content>
              <v-select
                :value="selectedObject.doorSwing"
                @input="updateProperty('doorSwing', $event)"
                :label="$t('$vuetify.floorPlan.doorSwing')"
                :items="doorSwingOptions"
                dense
                outlined
                hide-details
              ></v-select>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.thickness') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.height"
                @input="updateProperty('height', $event)"
                min="5"
                max="30"
                step="5"
                thumb-label
                dense
                hide-details
              >
                <template v-slot:append>
                  <span class="text-caption">{{ selectedObject.height }}px</span>
                </template>
              </v-slider>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.rotation') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.rotation || 0"
                @input="updateProperty('rotation', $event)"
                min="0"
                max="360"
                step="15"
                thumb-label
                dense
                hide-details
              >
                <template v-slot:append>
                  <span class="text-caption">{{ selectedObject.rotation || 0 }}°</span>
                </template>
              </v-slider>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.color') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.color"
                @input="updateProperty('color', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>
        </div>

        <!-- Bar-specific properties -->
        <div v-if="selectedObject.type === 'bar'">
          <v-subheader>{{ $t('$vuetify.floorPlan.barProperties') }}</v-subheader>

          <!-- Linked Table (moved to top) -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.linkedTable') }}
              </v-list-item-subtitle>
              <v-btn
                block
                class="link-table-btn"
                elevation="0"
                small
                @click="$emit('link-table', selectedObject)"
              >
                <v-icon left small>mdi-link</v-icon>
                {{ selectedObject.tableId ? $t('$vuetify.floorPlan.changeTable') : $t('$vuetify.floorPlan.linkTable') }}
              </v-btn>
              <div v-if="selectedObject.tableId" class="text-caption mt-1 grey--text">
                {{ $t('$vuetify.floorPlan.linked') }}
              </div>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.barName"
                @input="updateProperty('barName', $event)"
                :label="$t('$vuetify.floorPlan.barName')"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-select
                :value="selectedObject.shape"
                @input="updateProperty('shape', $event)"
                :label="$t('$vuetify.floorPlan.shape')"
                :items="barShapeOptions"
                dense
                outlined
                hide-details
              ></v-select>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.rotation') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.rotation || 0"
                @input="updateProperty('rotation', $event)"
                min="0"
                max="360"
                step="15"
                thumb-label
                dense
                hide-details
              >
                <template v-slot:append>
                  <span class="text-caption">{{ selectedObject.rotation || 0 }}°</span>
                </template>
              </v-slider>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.color') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.color"
                @input="updateProperty('color', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.borderColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.borderColor"
                @input="updateProperty('borderColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>
        </div>

        <!-- Area-specific properties -->
        <div v-if="selectedObject.type === 'area'">
          <v-subheader>{{ $t('$vuetify.floorPlan.areaProperties') }}</v-subheader>

          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.label"
                @input="updateProperty('label', $event)"
                :label="$t('$vuetify.floorPlan.label')"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.fontSize"
                @input="updateProperty('fontSize', parseInt($event))"
                :label="$t('$vuetify.floorPlan.fontSize')"
                type="number"
                dense
                outlined
                hide-details
                suffix="px"
                min="10"
                max="32"
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.backgroundColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.backgroundColor"
                @input="updateProperty('backgroundColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.opacity') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.opacity"
                @input="updateProperty('opacity', $event)"
                min="0.1"
                max="1"
                step="0.1"
                thumb-label
                dense
                hide-details
              ></v-slider>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content>
              <v-select
                :value="selectedObject.pattern || 'none'"
                @input="updateProperty('pattern', $event)"
                :label="$t('$vuetify.floorPlan.pattern')"
                :items="patternOptions"
                dense
                outlined
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-texture</v-icon>
                </template>
              </v-select>
            </v-list-item-content>
          </v-list-item>
        </div>

        <!-- Text Box Properties -->
        <div v-if="selectedObject.type === 'textbox'">
          <v-subheader>{{ $t('$vuetify.floorPlan.textBoxProperties') }}</v-subheader>

          <!-- Text content -->
          <v-list-item>
            <v-list-item-content>
              <v-textarea
                :value="selectedObject.text"
                @input="updateProperty('text', $event)"
                :label="$t('$vuetify.floorPlan.text')"
                dense
                outlined
                hide-details
                rows="3"
                auto-grow
              ></v-textarea>
            </v-list-item-content>
          </v-list-item>

          <!-- Font family -->
          <v-list-item>
            <v-list-item-content>
              <v-select
                :value="selectedObject.fontFamily"
                @input="updateProperty('fontFamily', $event)"
                :label="$t('$vuetify.floorPlan.fontFamily')"
                :items="fontFamilyOptions"
                dense
                outlined
                hide-details
              ></v-select>
            </v-list-item-content>
          </v-list-item>

          <!-- Font size -->
          <v-list-item>
            <v-list-item-content>
              <v-text-field
                :value="selectedObject.fontSize"
                @input="updateProperty('fontSize', parseInt($event))"
                :label="$t('$vuetify.floorPlan.fontSize')"
                type="number"
                dense
                outlined
                hide-details
                suffix="px"
                min="8"
                max="72"
              ></v-text-field>
            </v-list-item-content>
          </v-list-item>

          <!-- Font style buttons (Bold, Italic) -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                Stil fonta
              </v-list-item-subtitle>
              <v-btn-toggle
                :value="getFontStyleArray(selectedObject.fontStyle)"
                @change="updateFontStyle"
                multiple
                dense
              >
                <v-btn small value="bold">
                  <v-icon small>mdi-format-bold</v-icon>
                  {{ $t('$vuetify.floorPlan.bold') }}
                </v-btn>
                <v-btn small value="italic">
                  <v-icon small>mdi-format-italic</v-icon>
                  {{ $t('$vuetify.floorPlan.italic') }}
                </v-btn>
              </v-btn-toggle>
            </v-list-item-content>
          </v-list-item>

          <!-- Underline -->
          <v-list-item>
            <v-list-item-content>
              <v-checkbox
                :input-value="selectedObject.textDecoration === 'underline'"
                @change="updateProperty('textDecoration', $event ? 'underline' : '')"
                :label="$t('$vuetify.floorPlan.underline')"
                dense
                hide-details
              ></v-checkbox>
            </v-list-item-content>
          </v-list-item>

          <!-- Text alignment -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.textAlign') }}
              </v-list-item-subtitle>
              <v-btn-toggle
                :value="selectedObject.align"
                @change="updateProperty('align', $event)"
                mandatory
                dense
              >
                <v-btn small value="left">
                  <v-icon small>mdi-format-align-left</v-icon>
                </v-btn>
                <v-btn small value="center">
                  <v-icon small>mdi-format-align-center</v-icon>
                </v-btn>
                <v-btn small value="right">
                  <v-icon small>mdi-format-align-right</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-list-item-content>
          </v-list-item>

          <!-- Text color -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.textColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.textColor"
                @input="updateProperty('textColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <!-- Background color -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.backgroundColor') }}
              </v-list-item-subtitle>
              <v-checkbox
                :input-value="selectedObject.backgroundColor === 'transparent'"
                @change="updateProperty('backgroundColor', $event ? 'transparent' : '#ffffff')"
                :label="$t('$vuetify.floorPlan.transparent')"
                dense
                hide-details
                class="mb-2"
              ></v-checkbox>
              <v-color-picker
                v-if="selectedObject.backgroundColor !== 'transparent'"
                :value="selectedObject.backgroundColor"
                @input="updateProperty('backgroundColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <!-- Border color -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-2">
                {{ $t('$vuetify.floorPlan.borderColor') }}
              </v-list-item-subtitle>
              <v-color-picker
                :value="selectedObject.borderColor"
                @input="updateProperty('borderColor', $event)"
                hide-mode-switch
                mode="hexa"
                flat
                class="mx-auto"
              ></v-color-picker>
            </v-list-item-content>
          </v-list-item>

          <!-- Rotation -->
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption mb-1">
                {{ $t('$vuetify.floorPlan.rotation') }}
              </v-list-item-subtitle>
              <v-slider
                :value="selectedObject.rotation || 0"
                @input="updateProperty('rotation', $event)"
                min="0"
                max="360"
                step="15"
                thumb-label
                dense
                hide-details
              >
                <template v-slot:append>
                  <span class="text-caption">{{ selectedObject.rotation || 0 }}°</span>
                </template>
              </v-slider>
            </v-list-item-content>
          </v-list-item>
        </div>

        <v-divider class="my-2"></v-divider>

        <!-- Duplicate button -->
        <v-list-item>
          <v-btn
            block
            class="duplicate-object-btn"
            elevation="0"
            @click="$emit('duplicate-object', selectedObject)"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Dupliciraj (Ctrl+D)
          </v-btn>
        </v-list-item>

        <!-- Delete button -->
        <v-list-item>
          <v-btn
            block
            class="delete-object-btn"
            elevation="0"
            @click="$emit('delete-object', selectedObject)"
          >
            <v-icon left>mdi-delete</v-icon>
            {{ $t('$vuetify.floorPlan.delete') }}
          </v-btn>
        </v-list-item>
      </div>
    </v-list>

    <!-- Properties Help Dialog -->
    <v-dialog v-model="propertiesHelpDialog" max-width="700px" scrollable>
      <v-card class="properties-help-dialog-card">
        <div class="properties-help-dialog-title">
          <v-icon left color="white" size="28">mdi-help-circle</v-icon>
          <span class="properties-help-dialog-title-text">{{ $t('$vuetify.floorPlan.propertiesHelp') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="propertiesHelpDialog = false" class="properties-help-close-icon-btn">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-4">
          <v-expansion-panels accordion class="properties-help-expansion-panels">
            <!-- Position & Size -->
            <v-expansion-panel>
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-vector-arrange-below</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropPositionSize') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.position') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropPosition') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.size') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropSize') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.rotation') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropRotation') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Table Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'table'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-table-furniture</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropTableProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.linkedTable') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropLinkedTable') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.tableName') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTableName') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.shape') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTableShape') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.seats') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropSeats') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.tableColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTableColor') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.chairColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropChairColor') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Bar Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'bar'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-countertop</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropBarProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.linkedTable') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropLinkedTable') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.barName') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropBarName') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.shape') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropBarShape') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.color') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropBarColor') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.borderColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropBorderColor') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Wall Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'wall'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-wall</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropWallProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.thickness') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropThickness') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.color') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropWallColor') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Area Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'area'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-texture-box</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropAreaProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.label') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropAreaLabel') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.fontSize') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropFontSize') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.backgroundColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropBackgroundColor') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.opacity') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropOpacity') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.pattern') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropPattern') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Door Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'door'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-door</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropDoorProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.doorDirection') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropDoorDirection') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.color') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropDoorColor') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Text Properties -->
            <v-expansion-panel v-if="selectedObject && selectedObject.type === 'textbox'">
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-text-box</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropTextProperties') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>{{ $t('$vuetify.floorPlan.text') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropText') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.fontFamily') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropFontFamily') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.fontSize') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTextSize') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.textColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTextColor') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.backgroundColor') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTextBackground') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.textAlign') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTextAlign') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.bold') }}/{{ $t('$vuetify.floorPlan.italic') }}/{{ $t('$vuetify.floorPlan.underline') }}:</strong> {{ $t('$vuetify.floorPlan.helpPropTextStyle') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Actions -->
            <v-expansion-panel>
              <v-expansion-panel-header class="properties-help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-gesture-tap</v-icon>
                  <strong class="properties-help-panel-title">{{ $t('$vuetify.floorPlan.helpPropActions') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="properties-help-panel-content">
                <p><strong>Ctrl+D — Dupliciraj:</strong> {{ $t('$vuetify.floorPlan.helpPropDuplicate') }}</p>
                <p><strong>{{ $t('$vuetify.floorPlan.delete') }} (Delete):</strong> {{ $t('$vuetify.floorPlan.helpPropDelete') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn class="properties-help-close-btn" @click="propertiesHelpDialog = false">
            {{ $t('$vuetify.floorPlan.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'FloorPlanPropertiesPanel',
  props: {
    selectedObject: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      propertiesHelpDialog: false,
      shapeOptions: [
        { text: this.$t('$vuetify.floorPlan.circle'), value: 'circle' },
        { text: this.$t('$vuetify.floorPlan.square'), value: 'square' },
        { text: this.$t('$vuetify.floorPlan.rectangle'), value: 'rectangle' },
        { text: this.$t('$vuetify.floorPlan.oval'), value: 'oval' },
        { text: this.$t('$vuetify.floorPlan.corner'), value: 'corner' }
      ],
      cornerAngleOptions: [
        { text: this.$t('$vuetify.floorPlan.topLeft'), value: 0 },
        { text: this.$t('$vuetify.floorPlan.topRight'), value: 90 },
        { text: this.$t('$vuetify.floorPlan.bottomRight'), value: 180 },
        { text: this.$t('$vuetify.floorPlan.bottomLeft'), value: 270 }
      ],
      barShapeOptions: [
        { text: this.$t('$vuetify.floorPlan.rectangle'), value: 'rectangle' },
        { text: this.$t('$vuetify.floorPlan.semicircle'), value: 'semicircle' },
        { text: this.$t('$vuetify.floorPlan.semioval'), value: 'semioval' }
      ],
      doorSwingOptions: [
        { text: this.$t('$vuetify.floorPlan.doorLeftIn'), value: 'left-in' },
        { text: this.$t('$vuetify.floorPlan.doorLeftOut'), value: 'left-out' },
        { text: this.$t('$vuetify.floorPlan.doorRightIn'), value: 'right-in' },
        { text: this.$t('$vuetify.floorPlan.doorRightOut'), value: 'right-out' }
      ],
      patternOptions: [
        { text: this.$t('$vuetify.floorPlan.patternNone'), value: 'none' },
        { text: this.$t('$vuetify.floorPlan.patternWood'), value: 'wood' },
        { text: this.$t('$vuetify.floorPlan.patternParquet'), value: 'parquet' },
        { text: this.$t('$vuetify.floorPlan.patternTile'), value: 'tile' },
        { text: this.$t('$vuetify.floorPlan.patternCarpet'), value: 'carpet' },
        { text: this.$t('$vuetify.floorPlan.patternMarble'), value: 'marble' },
        { text: this.$t('$vuetify.floorPlan.patternConcrete'), value: 'concrete' }
      ],
      fontFamilyOptions: [
        { text: 'Arial', value: 'Arial' },
        { text: 'Helvetica', value: 'Helvetica' },
        { text: 'Times New Roman', value: 'Times New Roman' },
        { text: 'Georgia', value: 'Georgia' },
        { text: 'Courier New', value: 'Courier New' },
        { text: 'Verdana', value: 'Verdana' },
        { text: 'Comic Sans MS', value: 'Comic Sans MS' },
        { text: 'Impact', value: 'Impact' }
      ]
    }
  },
  methods: {
    updateProperty (key, value) {
      this.$emit('property-changed', { key, value })
    },
    getFontStyleArray (fontStyle) {
      const styles = []
      if (fontStyle && fontStyle.includes('bold')) styles.push('bold')
      if (fontStyle && fontStyle.includes('italic')) styles.push('italic')
      return styles
    },
    updateFontStyle (selectedStyles) {
      let newStyle = 'normal'
      if (selectedStyles && selectedStyles.length > 0) {
        newStyle = selectedStyles.join(' ')
      }
      this.updateProperty('fontStyle', newStyle)
    },
    getTypeColor (type) {
      switch (type) {
        case 'table':
          return 'blue'
        case 'wall':
          return 'grey'
        case 'door':
          return 'brown'
        case 'bar':
          return 'orange'
        case 'textbox':
          return 'purple'
        case 'area':
          return 'green'
        default:
          return 'grey'
      }
    },
    getTypeLabel (type) {
      switch (type) {
        case 'table':
          return this.$t('$vuetify.floorPlan.table')
        case 'wall':
          return this.$t('$vuetify.floorPlan.wall')
        case 'door':
          return this.$t('$vuetify.floorPlan.door')
        case 'bar':
          return this.$t('$vuetify.floorPlan.bar')
        case 'textbox':
          return this.$t('$vuetify.floorPlan.textBox')
        case 'area':
          return this.$t('$vuetify.floorPlan.area')
        default:
          return type
      }
    }
  }
}
</script>

<style scoped>
/* Floor Plan Properties Panel Glass Style */
.floor-plan-properties-panel {
  z-index: 5;
  height: 100%;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-left: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: -2px 0 12px rgba(0, 179, 190, 0.08) !important;
}

/* Properties Subheader */
.properties-subheader {
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  color: rgba(57, 137, 201, 0.9) !important;
  padding-top: 12px !important;
  padding-bottom: 8px !important;
}

/* Help Button */
.help-btn {
  transition: all 0.2s ease !important;
}

.help-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.1);
}

/* List Item Styling */
.v-list-item--dense {
  min-height: 36px !important;
}

/* Subheader Styling */
.v-subheader {
  font-weight: 700 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  color: rgba(57, 137, 201, 0.8) !important;
  letter-spacing: 0.5px !important;
  padding-top: 12px !important;
  padding-bottom: 6px !important;
}

/* Text Field and Select Styling */
.v-text-field--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.v-text-field--outlined:hover >>> fieldset,
.v-select--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.v-text-field--outlined.v-input--is-focused >>> fieldset,
.v-select--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.v-text-field >>> .v-input__slot,
.v-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
}

/* Slider Styling */
.v-slider >>> .v-slider__thumb {
  background: #3989C9 !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.3) !important;
  width: 20px !important;
  height: 20px !important;
  transition: transform 0.2s ease !important;
}

.v-slider >>> .v-slider__thumb:hover {
  transform: scale(1.1) !important;
}

.v-slider >>> .v-slider__track-fill {
  background: linear-gradient(90deg,
    rgba(0, 179, 190, 0.8) 0%,
    rgba(57, 137, 201, 0.8) 100%) !important;
  border-radius: 4px !important;
}

.v-slider >>> .v-slider__track-background {
  background: rgba(57, 137, 201, 0.15) !important;
  border-radius: 4px !important;
}

.v-slider >>> .v-slider__thumb-label {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.95) 100%) !important;
  color: white !important;
  font-weight: 600 !important;
}

/* Color Picker Styling */
.v-color-picker {
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.1) !important;
  border-radius: 8px !important;
  overflow: hidden;
}

/* Chip Badge Styling */
.v-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  text-transform: uppercase !important;
  font-size: 11px !important;
}

/* Link Table Button */
.link-table-btn {
  background: rgba(57, 137, 201, 0.12) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.3) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  transition: all 0.2s ease !important;
}

.link-table-btn:hover {
  background: rgba(57, 137, 201, 0.2) !important;
  border-color: rgba(57, 137, 201, 0.5) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.2) !important;
}

/* Duplicate Object Button */
.duplicate-object-btn {
  background: rgba(57, 137, 201, 0.08) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  transition: all 0.2s ease !important;
}

.duplicate-object-btn:hover {
  background: rgba(57, 137, 201, 0.15) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-1px);
}

/* Delete Object Button */
.delete-object-btn {
  background: rgba(244, 67, 54, 0.12) !important;
  color: #F44336 !important;
  border: 1px solid rgba(244, 67, 54, 0.3) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  transition: all 0.2s ease !important;
}

.delete-object-btn:hover {
  background: rgba(244, 67, 54, 0.2) !important;
  border-color: rgba(244, 67, 54, 0.5) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.25) !important;
}

/* Checkbox and Switch Styling */
.v-input--checkbox >>> .v-input--selection-controls__input {
  color: #3989C9 !important;
}

.v-input--switch >>> .v-input--switch__thumb {
  color: #3989C9 !important;
}

.v-input--switch >>> .v-input--switch__track {
  color: rgba(57, 137, 201, 0.3) !important;
}

/* Button Toggle Styling */
.v-btn-toggle >>> .v-btn {
  border-color: rgba(57, 137, 201, 0.3) !important;
  color: rgba(57, 137, 201, 0.8) !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.v-btn-toggle >>> .v-btn--active {
  background: rgba(57, 137, 201, 0.15) !important;
  color: #3989C9 !important;
  border-color: rgba(57, 137, 201, 0.5) !important;
}

.v-btn-toggle >>> .v-btn:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

/* Properties Help Dialog Styling */
.properties-help-dialog-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.12) !important;
}

.properties-help-dialog-title {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 24px !important;
  display: flex !important;
  align-items: center !important;
  position: relative;
}

.properties-help-dialog-title::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  pointer-events: none;
}

.properties-help-dialog-title-text {
  color: white !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  letter-spacing: 0.5px !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
}

.properties-help-close-icon-btn {
  position: relative;
  z-index: 1;
}

.properties-help-expansion-panels >>> .v-expansion-panel {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
  margin-bottom: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.1) !important;
  overflow: hidden;
}

.properties-help-expansion-panels >>> .v-expansion-panel:hover {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.properties-help-expansion-panels >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.properties-help-panel-header {
  padding: 16px !important;
  min-height: 56px !important;
}

.properties-help-panel-header >>> .v-expansion-panel-header__icon .v-icon {
  color: rgba(57, 137, 201, 0.6) !important;
  font-size: 20px !important;
}

.properties-help-panel-title {
  color: rgba(70, 70, 80, 0.95) !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
}

.properties-help-panel-content {
  padding: 16px 16px 20px 16px !important;
  background: rgba(255, 255, 255, 0.4) !important;
}

.properties-help-panel-content >>> p {
  font-size: 14px !important;
  color: rgba(70, 70, 80, 0.9) !important;
  line-height: 1.6 !important;
  margin-bottom: 12px !important;
}

.properties-help-panel-content >>> p:last-child {
  margin-bottom: 0 !important;
}

.properties-help-close-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.properties-help-close-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}
</style>
